import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '../../lib/cn';

/**
 * MessageScroller — solves the classic streaming-scroll problem (shadcn's new
 * conversation layer). Wrap a message list and turn on `autoScroll`: the
 * viewport pins to the bottom as tokens arrive, but backs off the moment the
 * reader scrolls up — preserving their position — and re-pins when they return
 * to the bottom. Built on Radix ScrollArea (keyboard + a11y for free).
 */
const AT_BOTTOM_PX = 48;

export interface MessageScrollerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'dir'> {
  autoScroll?: boolean;
}

export const MessageScroller = React.forwardRef<
  HTMLDivElement,
  MessageScrollerProps
>(({ className, autoScroll = true, children, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const pinnedRef = React.useRef(true);

  const handleScroll = React.useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedRef.current = distanceFromBottom <= AT_BOTTOM_PX;
  }, []);

  React.useEffect(() => {
    if (!autoScroll) return;
    const el = viewportRef.current;
    if (!el) return;
    const pinToBottom = () => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    };
    const observer = new MutationObserver(pinToBottom);
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    pinToBottom();
    return () => observer.disconnect();
  }, [autoScroll]);

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn('tw:relative tw:overflow-hidden', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        onScroll={handleScroll}
        className="tw:h-full tw:w-full tw:rounded-[inherit]"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollAreaPrimitive.Scrollbar
        orientation="vertical"
        className="tw:flex tw:w-2 tw:touch-none tw:select-none tw:p-0.5"
      >
        <ScrollAreaPrimitive.Thumb className="tw:flex-1 tw:rounded-full tw:bg-[var(--border-strong)]" />
      </ScrollAreaPrimitive.Scrollbar>
    </ScrollAreaPrimitive.Root>
  );
});
MessageScroller.displayName = 'MessageScroller';
